import { isCreatableActivityWorkPath } from '@internal/spassu-code-contract';
import type {
  ActivityWorkPath,
  AzureWorkItemDetail,
  CreateActivityInput,
  Issue,
  ScmProviderDescriptor,
} from '../../api/types';
import {
  composeActivityDescription,
  validateActivityTitle,
  ActivityCreationContentState,
} from '../../activity-creation/activityCreationReducer';
import type { RepoHierarchyValue } from './RepoHierarchyPicker';
import type { CreationRepositoryValue } from './useCreationRepository';

export interface CreationDraft {
  provider: ScmProviderDescriptor | null;
  repository: CreationRepositoryValue;
  hierarchy: RepoHierarchyValue;
  issue: Issue | null;
  workItem: AzureWorkItemDetail | null;
  content: ActivityCreationContentState;
  requireTargetBranch: boolean;
  workPath: ActivityWorkPath;
}

function isAgnostic(draft: CreationDraft) {
  return !!draft.provider && draft.provider.id !== 'azure-devops';
}

function repositoryErrors(draft: CreationDraft): Record<string, string> {
  const errors: Record<string, string> = {};
  if (isAgnostic(draft)) {
    const provider = draft.provider!;
    if (!draft.hierarchy.owner)
      errors.owner = `Selecione um(a) ${provider.hierarchy.ownerLabel}`;
    if (provider.hierarchy.hasProject && !draft.hierarchy.project)
      errors.project = `Selecione um(a) ${
        provider.hierarchy.projectLabel ?? 'Project'
      }`;
    if (!draft.hierarchy.repository) errors.repo = 'Selecione um repositório';
  } else {
    if (!draft.repository.project) errors.project = 'Selecione um projeto';
    if (!draft.repository.repository) errors.repo = 'Selecione um repositório';
  }
  return errors;
}

export function validateCreationDraft(draft: CreationDraft) {
  const errors = repositoryErrors(draft);
  const titleError = validateActivityTitle(draft.content.title.value);
  if (titleError) errors.title = titleError;
  if (!isCreatableActivityWorkPath(draft.workPath))
    errors.workPath = 'Selecione uma via de trabalho';
  const branch = isAgnostic(draft)
    ? draft.hierarchy.branch?.name
    : draft.repository.branch;
  if (draft.requireTargetBranch && !branch)
    errors.branch = 'Selecione a branch base/destino do PR';
  return errors;
}

function providerInput(draft: CreationDraft): Partial<CreateActivityInput> {
  return {
    provider: draft.provider!.id,
    repoOwner: draft.hierarchy.owner!.id,
    repoProject: draft.hierarchy.project?.name ?? null,
    repoName: draft.hierarchy.repository!.name,
    branch: draft.hierarchy.branch?.name || undefined,
    issueExternalId: draft.issue?.externalId,
    issueType: draft.issue?.type,
    issueTitle: draft.issue?.title,
    issueState: draft.issue?.state,
    issueUrl: draft.issue?.url,
  };
}

function legacyInput(draft: CreationDraft): Partial<CreateActivityInput> {
  return {
    azureProject: draft.repository.project,
    azureRepository: draft.repository.repository,
    branch: draft.repository.branch || undefined,
    azureWorkItemId: draft.workItem?.id,
    azureWorkItemType: draft.workItem?.type,
    azureWorkItemUrl: draft.workItem?.url,
    azureWorkItemTitle: draft.workItem?.title,
    azureWorkItemState: draft.workItem?.state,
  };
}

/** Called only after validation; keeps the existing provider-specific wire fields. */
export function buildCreationInput(draft: CreationDraft): CreateActivityInput {
  return {
    title: draft.content.title.value.trim(),
    description: composeActivityDescription(
      draft.content.description.value,
      draft.content.description.lastGenerated,
    ),
    workPath: draft.workPath,
    ...(isAgnostic(draft) ? providerInput(draft) : legacyInput(draft)),
  };
}
